import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MessageSquare, ChevronDown, Loader2 } from 'lucide-react';
import { reviewsApi } from '../../lib/api';
import { ReviewCard } from './ReviewCard';
import { ReviewStats } from './ReviewStats';
import { ReviewForm } from './ReviewForm';

interface ReviewSectionProps {
    productId: string;
}

export function ReviewSection({ productId }: ReviewSectionProps) {
    const [showForm, setShowForm] = useState(false);
    const [page, setPage] = useState(1);
    const limit = 5;

    // Fetch review stats
    const { data: stats, isLoading: statsLoading } = useQuery({
        queryKey: ['reviewStats', productId],
        queryFn: () => reviewsApi.getStats(productId),
        staleTime: 1000 * 60 * 5,
    });

    // Fetch reviews
    const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
        queryKey: ['reviews', productId, page],
        queryFn: () => reviewsApi.getByProduct(productId, page, limit),
        staleTime: 1000 * 60 * 5,
    });

    const reviews = reviewsData?.data || [];
    const totalPages = reviewsData?.meta?.totalPages || 1;

    return (
        <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold font-display flex items-center gap-2">
                    <MessageSquare className="w-6 h-6 text-primary-500" />
                    Avis Clients
                    {stats && stats.totalCount > 0 && (
                        <span className="text-sm font-normal text-gray-500">
                            ({stats.totalCount})
                        </span>
                    )}
                </h3>
                <button
                    onClick={() => setShowForm(!showForm)}
                    className="btn-outline btn-sm"
                >
                    {showForm ? 'Annuler' : 'Écrire un avis'}
                </button>
            </div>

            {/* Stats */}
            {statsLoading ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
                </div>
            ) : stats && stats.totalCount > 0 ? (
                <ReviewStats stats={stats} />
            ) : null}

            {/* Review Form */}
            {showForm && (
                <ReviewForm
                    productId={productId}
                    onSuccess={() => setShowForm(false)}
                />
            )}

            {/* Reviews List */}
            {reviewsLoading ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
                </div>
            ) : reviews.length > 0 ? (
                <div className="space-y-4">
                    {reviews.map((review) => (
                        <ReviewCard key={review.id} review={review} />
                    ))}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center gap-2 pt-4">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                                (pageNum) => (
                                    <button
                                        key={pageNum}
                                        onClick={() => setPage(pageNum)}
                                        className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${page === pageNum
                                                ? 'bg-primary-500 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {pageNum}
                                    </button>
                                )
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center py-8 space-y-3">
                    <MessageSquare className="w-12 h-12 text-gray-200 mx-auto" />
                    <p className="text-gray-500">Aucun avis pour le moment</p>
                    {!showForm && (
                        <button
                            onClick={() => setShowForm(true)}
                            className="text-primary-500 font-medium hover:underline"
                        >
                            Soyez le premier à donner votre avis!
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
